import { useMemo } from "react"
import markdownit from "markdown-it"
import { GetApp } from "../App"
import { Localization, LocalizationMessages, LocalizationReplaceText, LanguageTextReplacements } from "../App/Localization"
import { classNames } from "../Components"

interface MessageCallback {
  (message: LocalizationMessages): string | undefined;
}

interface LocalizedMessageProps {
  className?: string;
  html?: boolean;
  message: MessageCallback;
  replace?: LanguageTextReplacements;
  // Named Markdown destinations are resolved as attributes, never interpolated HTML.
  links?: Record<string, string | undefined>;
}

export function LocalizedMessage({ className, html, message, replace, links }: LocalizedMessageProps) {
  const app = GetApp();
  const text = useMemo(() => {
    const messages = Localization.Messages[app.language] || {};
    const md = markdownit({ html: html || false });
    const defaultLinkOpen = md.renderer.rules.link_open
      ?? ((tokens, index, options, _environment, self) => self.renderToken(tokens, index, options));
    md.renderer.rules.link_open = (tokens, index, options, environment, self) => {
      const token = tokens[index];
      const name = token.attrGet("href");
      if (name && links && Object.hasOwn(links, name)) {
        const href = links[name];
        if (href !== undefined) {
          if (!md.validateLink(href)) throw new Error("Invalid localized link destination");
          token.attrSet("href", href);
          token.attrSet("target", "_blank");
          token.attrSet("rel", "noopener noreferrer");
        } else {
          token.attrs = token.attrs?.filter(([name]) => name !== "href") ?? [];
          token.attrSet("role", "link");
          token.attrSet("aria-disabled", "true");
          token.attrSet("tabindex", "-1");
        }
      }
      return defaultLinkOpen(tokens, index, options, environment, self);
    };
    const text = LocalizationReplaceText(message(messages) || "", replace);
    return md.render(text);
  }, [message, app.language, html, replace, links]);
  return <div className={classNames(["LocalizedMessage", className])} dangerouslySetInnerHTML={{ __html: text }} />;
}
